import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

import './button.scss'

class ButtonComponent extends PureComponent {
	state = {
		displayFeedback: false,
	}

	componentDidUpdate(prevProps) {
		this.changeState(prevProps)
	}

	changeState = prevProps => {
		const { wasSuccessful } = this.props

		if (
			wasSuccessful !== prevProps.wasSuccessful &&
			wasSuccessful === true
		) {
			this.setState({
				displayFeedback: true,
			})

			setTimeout(() => {
				this.setState({
					displayFeedback: false,
				})
			}, 800)
		}
	}

	onClick = e => {
		const { onClick, isLoading } = this.props

		const { displayFeedback } = this.state

		if (displayFeedback === false && isLoading === false && onClick) {
			onClick(e)
		}
	}

	render() {
		const {
			className,
			onClick,
			type,
			text,
			metaContent,
			icon,
			fullWidth,
			centerContent,
			size,
			layout,
			showFeedback,
			isLoading,
			wasSuccessful,
			hasFailed,
			isVisible,
			highContrast,
			label,
			forwardedRef,
			href,
			to,
			colorTheme,
			...rest
		} = this.props

		const { displayFeedback } = this.state

		const buttonClass = 'o-button'
		const cssClasses = classNames(
			buttonClass,
			layout && `${buttonClass}--${layout}`,
			size && `${buttonClass}--${size}`,
			!text && icon && `${buttonClass}--icon-only`,
			fullWidth && `${buttonClass}--full-width`,
			centerContent && `${buttonClass}--centered`,
			highContrast && `${buttonClass}--high-contrast`,
			colorTheme && `${buttonClass}--color-theme-${colorTheme}`,
			icon && `has-icon`,
			{ 'is-loading': isLoading },
			{ 'was-successful': displayFeedback },
			{ 'has-failed': hasFailed },
			{ 'is-visible': isVisible || displayFeedback },
			className
		)

		let ButtonTag = 'button'

		if (to) {
			ButtonTag = Link
		} else if (href) {
			ButtonTag = 'a'
		}

		return (
			<ButtonTag
				ref={forwardedRef}
				to={to}
				className={cssClasses}
				onClick={this.onClick}
				href={href}
				type={!href && !to ? type : undefined}
				{...rest}
			>
				{showFeedback && (
					<span className={`${buttonClass}__feedback`}>
						<span className={`${buttonClass}__spinner`} />
						<span className={`${buttonClass}__success`} />
						<span className={`${buttonClass}__failed`} />
					</span>
				)}

				{icon && <span className={`${buttonClass}__icon`}>{icon}</span>}

				{text && (
					<span className={`${buttonClass}__text`}>
						<span>{text}</span>
						{label && label}
					</span>
				)}

				{metaContent && (
					<span className={`${buttonClass}__meta-content`}>
						{metaContent}
					</span>
				)}
			</ButtonTag>
		)
	}
}

ButtonComponent.propTypes = {
	className: PropTypes.string,
	onClick: PropTypes.func,
	href: PropTypes.string,
	to: PropTypes.string,
	type: PropTypes.oneOf(['button', 'reset', 'submit']),
	text: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.node,
	]),
	metaContent: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.node,
	]),
	icon: PropTypes.node,
	fullWidth: PropTypes.bool,
	centerContent: PropTypes.bool,
	size: PropTypes.oneOf(['small']),
	layout: PropTypes.oneOf([
		'primary',
		'secondary',
		'tertiary',
		'ghosted',
		'warning',
		'text',
		'underlined-text',
	]),
	showFeedback: PropTypes.bool,
	isLoading: PropTypes.bool,
	wasSuccessful: PropTypes.bool,
	hasFailed: PropTypes.bool,
	isVisible: PropTypes.bool,
	highContrast: PropTypes.bool,
	label: PropTypes.node,
	colorTheme: PropTypes.oneOf(['info', 'warning', 'success']),
}

ButtonComponent.defaultProps = {
	className: null,
	onClick: null,
	href: null,
	to: null,
	type: 'button',
	text: null,
	metaContent: null,
	icon: null,
	fullWidth: false,
	centerContent: false,
	size: null,
	layout: null,
	showFeedback: false,
	isLoading: false,
	wasSuccessful: null,
	hasFailed: null,
	isVisible: true,
	highContrast: false,
	label: null,
	colorTheme: null,
}

const Button = React.forwardRef((props, ref) => (
	<ButtonComponent {...props} forwardedRef={ref} />
))

export default Button
